/**
 * Render a string containing the inline-markdown subset our backend content
 * uses (`**bold**`, `*italic*`, `` `code` ``) as React nodes — no dependency,
 * no HTML parsing/injection. Line breaks are left in the string; pair with
 * `whitespace-pre-line` on the container. If richer markdown ever lands in the
 * content contract (lists, links, tables), swap call sites to react-markdown
 * (see docs/api-requests/question/OPEN-2026-07-19-help-faq-answer-format.md).
 */

import type { ReactNode } from "react";

/** Matches the inline tokens we render: **bold**, *italic*, `code`. */
const TOKEN_PATTERN = /(\*\*[^*]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

export default function renderInlineMarkdown(text: string): ReactNode[] {
  // split() with a capturing group keeps the matched tokens in the array, so
  // the map below sees plain segments and tokens interleaved in order.
  return text
    .split(TOKEN_PATTERN)
    .filter(Boolean)
    .map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static positional segments of one string
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static positional segments of one string
          <code key={i} className="font-geist-mono text-[0.9em]">
            {part.slice(1, -1)}
          </code>
        );
      }
      if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static positional segments of one string
          <em key={i}>{part.slice(1, -1)}</em>
        );
      }
      return part;
    });
}
